import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { Code, Copy, CheckCircle, XCircle, Server, Loader, AlertTriangle } from 'lucide-react';

const RouterSetupWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const initializingRef = useRef(false); // Prevenir doble ejecución en React StrictMode
  
  // Detectar modo reconexión
  const reconnectMode = location.state?.reconnectMode || false;
  const selectedRouter = location.state?.selectedRouter || null;
  
  const [routers, setRouters] = useState([]);
  const [currentRouterIndex, setCurrentRouterIndex] = useState(0);
  const [step, setStep] = useState('copy-script'); // select, script, verify, connect, confirm, success
  const [copied, setCopied] = useState(false);
  const [vpnConnected, setVpnConnected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializingVPN, setInitializingVPN] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [routerInfo, setRouterInfo] = useState(null);
  const [hotspotValidation, setHotspotValidation] = useState(null);
  const [editableRouterName, setEditableRouterName] = useState('');
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [connectionData, setConnectionData] = useState({
    ip_address: '',
    username: 'ETWEB',
    password: '',
    port: 8728
  });

  useEffect(() => {
    // Prevenir doble ejecución en React StrictMode
    if (initializingRef.current) return;
    initializingRef.current = true;
    
    initializeAndLoadRouters();
  }, []);

  const initializeAndLoadRouters = async () => {
    try {
      // PRIORIDAD 1: Si viene con selectedRouter en el state, usar esos datos directamente
      if (selectedRouter) {
        console.log('🎯 Router recibido por navegación:', selectedRouter);
        
        // Establecer datos de conexión desde lo que pasó RoutersPage
        setRouters([selectedRouter]);
        setConnectionData({
          ip_address: selectedRouter.ip_address || selectedRouter.internal_ip,
          username: selectedRouter.username || 'ETWEB',
          password: selectedRouter.password,
          port: selectedRouter.port || 8728
        });
        
        // Si es modo reconexión, poner nombre editable
        if (reconnectMode) {
          setEditableRouterName(selectedRouter.name || `Router ${selectedRouter.router_index}`);
        }
        
        setStep('copy-script');
        setLoading(false);
        return;
      }
      
      // PRIORIDAD 2: Si NO recibió datos, cargar desde el API
      const response = await api.get('/api/licenses/my-routers');
      const pendingRouters = response.data.routers.filter(r => !r.router_configured);
      
      // Si ya hay routers con credenciales, ir directo a copiar script
      if (pendingRouters.length > 0 && pendingRouters[0].vpn_username) {
        setRouters(pendingRouters);
        setConnectionData({
          ip_address: pendingRouters[0].vpn_internal_ip,
          username: 'ETWEB',
          password: pendingRouters[0].mikrotik_password,
          port: 8728
        });
        setStep('copy-script');
      } else {
        // No hay credenciales aún, quedarse en paso 'generate-vpn'
        setStep('generate-vpn');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar routers:', error);
      setLoading(false);
      alert('Error al cargar información de routers');
    }
  };

  const loadRouters = async () => {
    try {
      const response = await api.get('/api/licenses/my-routers');
      const pendingRouters = response.data.routers.filter(r => !r.router_configured);
      
      if (pendingRouters.length === 0) {
        setRouters([]);
        setLoading(false);
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
        return;
      }
      
      setRouters(pendingRouters);
      
      // Pre-cargar datos del primer router
      if (pendingRouters.length > 0) {
        setConnectionData({
          ip_address: pendingRouters[0].vpn_internal_ip,  // IP interna en la red VPN
          username: 'ETWEB',
          password: pendingRouters[0].mikrotik_password,
          port: 8728  // Puerto API estándar
        });
      }
      
      setStep('copy-script');
    } catch (error) {
      console.error('Error loading routers:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentRouter = routers[currentRouterIndex];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVpnVerification = (connected) => {
    setVpnConnected(connected);
    if (connected) {
      setStep('connect');
    }
  };

  const handleTestConnection = async () => {
    console.log('🔄 Iniciando conexión con router...');
    console.log('📍 Datos de conexión:', connectionData);
    console.log('📍 License ID:', currentRouter.license_id);
    
    setConnecting(true);
    try {
      // Primero hacer diagnóstico y obtener info del router
      console.log('🔍 Ejecutando diagnóstico de conexión...');
      const diagResponse = await api.post('/api/routers/test-connection', {
        name: reconnectMode ? selectedRouter.name : `Router ${currentRouter.router_index}`,
        ip_address: connectionData.ip_address,
        username: connectionData.username,
        password: connectionData.password,
        port: parseInt(connectionData.port)
      });
      
      console.log('📊 Diagnóstico:', diagResponse.data);
      
      // Mostrar resultados del diagnóstico
      if (!diagResponse.data.api_responds) {
        const details = diagResponse.data.details.join('\n');
        
        // En modo reconexión, mostrar modal de reintentar
        if (reconnectMode) {
          setConnecting(false);
          setShowRetryModal(true);
          return;
        }
        
        // Modo normal
        alert(`🔍 Diagnóstico de Conexión:\n\n${details}\n\n💡 Solución: Ejecuta el script completo en tu Mikrotik (incluyendo las líneas de API)`);
        setConnecting(false);
        return;
      }
      
      // Si el diagnóstico es exitoso, obtener info del router y mostrar confirmación
      console.log('✅ Conexión exitosa, obteniendo información del router...');
      setRouterInfo(diagResponse.data);
      
      // Guardar validación de hotspot si viene en la respuesta
      if (diagResponse.data.hotspot_validation) {
        console.log('🔍 Validación Hotspot recibida:', diagResponse.data.hotspot_validation);
        setHotspotValidation(diagResponse.data.hotspot_validation);
      }
      
      // En modo reconexión, ir directo a sincronizar planes
      if (reconnectMode) {
        await syncPlansAfterReconnect(selectedRouter.id);
        return;
      }
      
      // Modo normal: pedir nombre del router
      setEditableRouterName('');
      setStep('confirm');
      
    } catch (error) {
      console.error('❌ Error al conectar router:', error);
      console.error('❌ Error response:', error.response);
      
      // En modo reconexión, mostrar modal de reintentar
      if (reconnectMode) {
        setConnecting(false);
        setShowRetryModal(true);
        return;
      }
      
      const errorMsg = error.response?.data?.detail || 'Error al conectar con el router';
      alert(errorMsg);
    } finally {
      setConnecting(false);
    }
  };

  const handleGenerateVPNCredentials = async () => {
    console.log('🔑 Generando credenciales VPN...');
    setInitializingVPN(true);
    
    try {
      // Llamar al endpoint para crear credenciales
      const vpnResponse = await api.post('/api/licenses/initialize-vpn');
      console.log('✅ Credenciales VPN creadas:', vpnResponse.data);
      
      // Recargar routers con credenciales actualizadas
      const updatedResponse = await api.get('/api/licenses/my-routers');
      const pendingRouters = updatedResponse.data.routers.filter(r => !r.router_configured);
      
      if (pendingRouters.length > 0) {
        setRouters(pendingRouters);
        setConnectionData({
          ip_address: pendingRouters[0].vpn_internal_ip,
          username: 'ETWEB',
          password: pendingRouters[0].mikrotik_password,
          port: 8728
        });
        
        // Avanzar al siguiente paso: copiar script
        setStep('copy-script');
      } else {
        alert('No se pudieron cargar los routers. Por favor intenta nuevamente.');
      }
    } catch (error) {
      console.error('❌ Error generando credenciales VPN:', error);
      const errorMsg = error.response?.data?.detail || 'Error al generar credenciales VPN';
      alert(errorMsg);
    } finally {
      setInitializingVPN(false);
    }
  };

    const handleConfirmRouter = async () => {
    // Validar que el nombre no esté vacío
    if (!editableRouterName || editableRouterName.trim() === '') {
      alert('⚠️ Por favor asigna un nombre al router antes de continuar');
      return;
    }
    
    setConnecting(true);
    try {
      // En modo reconexión, el router ya existe, solo necesitamos sincronizar planes
      if (reconnectMode) {
        console.log('🔄 Modo reconexión: Sincronizando planes...');
        await syncPlansAfterReconnect(selectedRouter.id);
        return;
      }
      
      // MODO NORMAL: Crear router en el sistema
      const response = await api.post('/api/routers', {
        name: editableRouterName.trim(),
        ip_address: connectionData.ip_address,
        username: connectionData.username,
        password: connectionData.password,
        port: parseInt(connectionData.port),
        license_id: currentRouter.license_id,
        router_name: routerInfo.router_name,
        platform: routerInfo.platform,
        version: routerInfo.version,
        model: routerInfo.platform
      });
      
      console.log('✅ Router guardado exitosamente:', response.data);
      
      // Guardar validación de hotspot si viene en la respuesta
      if (response.data.hotspot_validation) {
        setHotspotValidation(response.data.hotspot_validation);
      }
      
      setStep('success');
    } catch (error) {
      console.error('❌ Error al guardar router:', error);
      alert(error.response?.data?.detail || 'Error al guardar el router');
    } finally {
      setConnecting(false);
    }
  };
  
  const syncPlansAfterReconnect = async (routerId) => {
    try {
      // 1. Analizar qué planes faltan
      const analysisResponse = await api.post(`/api/routers/${routerId}/analyze-sync`);
      const analysis = analysisResponse.data;
      
      console.log('📊 Análisis de sincronización:', analysis);
      
      // 2. Si no hay planes faltantes, éxito inmediato
      if (analysis.missing === 0) {
        setStep('success');
        setConnecting(false);
        return;
      }
      
      // 3. Sincronizar planes en lotes
      for (let i = 0; i < analysis.total_batches; i++) {
        const batchNumber = i + 1;
        const batchPlans = analysis.batches[i];
        
        console.log(`📦 Sincronizando lote ${batchNumber}/${analysis.total_batches}...`);
        
        await api.post(`/api/routers/${routerId}/sync-batch`, {
          batch_number: batchNumber,
          plans: batchPlans
        });
      }
      
      console.log('✅ Sincronización completada');
      setStep('success');
      setConnecting(false);
    } catch (error) {
      console.error('❌ Error sincronizando planes:', error);
      setConnecting(false);
      // Mostrar modal de reintentar
      setShowRetryModal(true);
    }
  };

  const handleContinue = () => {
    if (currentRouterIndex < routers.length - 1) {
      // Hay más routers pendientes
      if (window.confirm('¿Deseas configurar el siguiente router ahora?')) {
        setCurrentRouterIndex(currentRouterIndex + 1);
        setStep('copy-script');
        setVpnConnected(null);
        setCopied(false);
        
        // Pre-cargar datos del siguiente router (usando NAT externo)
        const nextRouter = routers[currentRouterIndex + 1];
        setConnectionData({
          ip_address: nextRouter.vpn_internal_ip,  // IP interna VPN
          username: 'ETWEB',
          password: nextRouter.mikrotik_password,
          port: 8728  // Puerto API
        });
      } else {
        // Navegar al dashboard
        navigate('/dashboard', { replace: true });
      }
    } else {
      // Navegar al dashboard
      navigate('/dashboard', { replace: true });
    }
  };

  if (loading || initializingVPN) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${
        isDark ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <Loader className="w-12 h-12 animate-spin text-blue-600 mb-4" />
        {initializingVPN && (
          <div className="text-center">
            <p className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Creando credenciales VPN en servidor Mikrotik...
            </p>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Esto puede tomar unos segundos
            </p>
          </div>
        )}
      </div>
    );
  }

  if (!currentRouter) {
    return null;
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' : 'bg-gray-50'
    }`}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Router {currentRouterIndex + 1} de {routers.length}
            </span>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {step === 'copy-script' && 'Paso 1/4: Script VPN'}
              {step === 'verify' && 'Solución de Problemas'}
              {step === 'connect' && 'Paso 2/4: Conexión'}
              {step === 'confirm' && 'Paso 3/4: Confirmación'}
              {step === 'success' && 'Completado'}
            </span>
          </div>
          <div className={`w-full rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{
                width: step === 'copy-script' ? '25%' : step === 'connect' ? '50%' : step === 'confirm' ? '75%' : '100%'
              }}
            />
          </div>
        </div>

        {/* Script Step */}
        {/* PASO 1: Generar Credenciales VPN */}
        {step === 'generate-vpn' && (
          <div className={`rounded-xl shadow-lg p-8 transition-colors duration-300 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-600 p-3 rounded-lg">
                <Server className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Paso 1: Generar Credenciales VPN
                </h2>
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Primero necesitamos crear tus credenciales en el servidor VPN
                </p>
              </div>
            </div>

            <div className={`rounded-lg p-6 mb-6 ${isDark ? 'bg-blue-900/30 border-2 border-blue-500' : 'bg-blue-50 border-2 border-blue-300'}`}>
              <h3 className={`font-semibold text-lg mb-3 ${isDark ? 'text-blue-300' : 'text-blue-900'}`}>
                ℹ️ ¿Qué vamos a hacer?
              </h3>
              <ul className={`space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">1.</span>
                  <span>Crear tus credenciales VPN únicas en el servidor MikroTik</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">2.</span>
                  <span>Asignarte una IP interna y puerto externo exclusivos</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">3.</span>
                  <span>Este proceso solo se hace UNA vez (las credenciales se reutilizan)</span>
                </li>
              </ul>
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleGenerateVPNCredentials}
                disabled={initializingVPN}
                className={`px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 flex items-center gap-3 ${
                  initializingVPN
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                }`}
              >
                {initializingVPN ? (
                  <>
                    <Loader className="w-6 h-6 animate-spin" />
                    <span>Generando credenciales...</span>
                  </>
                ) : (
                  <>
                    <Server className="w-6 h-6" />
                    <span>🔑 Generar mis Credenciales VPN</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {step === 'copy-script' && (
          <div className={`rounded-xl shadow-lg p-8 transition-colors duration-300 ${
            isDark ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-600 p-3 rounded-lg">
                <Code className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Paso 2: Copia el Script en tu MikroTik
                </h2>
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Router {currentRouter.router_index}
                </p>
              </div>
            </div>

            <div className={`rounded-lg p-4 mb-6 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Script de Configuración VPN
              </h3>
              <div style={{ 
                backgroundColor: '#000000',
                padding: '16px',
                borderRadius: '8px',
                border: '2px solid #00ff00'
              }}>
                <pre 
                  ref={(el) => {
                    if (el) {
                      el.style.setProperty('color', '#00ff00', 'important');
                      el.style.setProperty('background-color', '#000000', 'important');
                    }
                  }}
                  style={{ 
                    margin: '0',
                    padding: '0',
                    fontSize: '14px',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowX: 'auto',
                    lineHeight: '1.5'
                  }}
                >{currentRouter.script}</pre>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => copyToClipboard(currentRouter.script)}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  <Copy className="w-5 h-5" />
                  {copied ? '✓ Copiado!' : 'Copiar Script'}
                </button>
              </div>
            </div>

            {/* Recuadro de Recomendaciones Importantes */}
            <div className={`border-l-4 border-amber-500 rounded-lg p-6 mb-6 ${
              isDark ? 'bg-amber-900/20' : 'bg-amber-50'
            }`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className={`font-bold mb-3 text-lg ${
                    isDark ? 'text-amber-300' : 'text-amber-900'
                  }`}>
                    Importante, se recomienda tomar en cuenta:
                  </h4>
                  <ul className={`space-y-2 ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
                    <li className="flex gap-2">
                      <span className="font-bold">•</span>
                      <span>El Mikrotik debe estar correctamente configurado como hotspot y con internet</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold">•</span>
                      <span>De preferencia recién configurado</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold">•</span>
                      <span>Si tienes una configuración actual, user profiles, no hagas planes con el mismo nombre para evitar problemas</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold">•</span>
                      <span>Si tienes otras interfaces VPN, eso puede alterar el funcionamiento o la conexión de la VPN de EasyTickets</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold">•</span>
                      <span>Si balanceas, puede que no conecte o se desconecte seguido la conexión del VPN</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className={`rounded-lg p-4 mb-6 ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
              <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Instrucciones
              </h4>
              <ol className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {currentRouter.instructions.map((instruction, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span className="font-semibold text-blue-600 flex-shrink-0">{idx + 1}.</span>
                    <span>{instruction.replace(/^\d+\.\s*/, '')}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className={`border-2 rounded-lg p-4 mb-6 ${
              isDark 
                ? 'bg-amber-900/20 border-amber-700' 
                : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                  <p className="font-semibold mb-1">Verifica el status de la conexión VPN:</p>
                  <p>En tu Mikrotik, ve a <span className="font-mono font-bold">Interfaces → L2TP Client → VPN ETWEB</span></p>
                  <p>El campo <span className="font-mono font-bold">Status</span> debe decir: <span className="font-bold text-green-700">connected</span></p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ¿El status ya dice "connected"?
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setStep('connect')}
                  className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  <CheckCircle className="w-5 h-5" />
                  Sí, está conectado
                </button>
                <button
                  onClick={() => setStep('verify')}
                  className="flex items-center gap-2 px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                >
                  <XCircle className="w-5 h-5" />
                  No, tengo problemas
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Troubleshooting Step */}
        {step === 'verify' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="inline-flex p-4 bg-red-100 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Problemas de Conexión VPN</h2>
              <p className="text-gray-600">Sigue estas recomendaciones para resolver el problema</p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="border-l-4 border-blue-500 bg-blue-50 p-4">
                <h4 className="font-semibold text-gray-900 mb-2">✓ Verifica otras conexiones VPN</h4>
                <p className="text-sm text-gray-700">Asegúrate de que tu Mikrotik no tenga otra conexión VPN activa que esté bloqueando el enlace</p>
              </div>

              <div className="border-l-4 border-green-500 bg-green-50 p-4">
                <h4 className="font-semibold text-gray-900 mb-2">✓ Verifica conexión a Internet</h4>
                <p className="text-sm text-gray-700">Tu Mikrotik debe tener acceso a Internet para conectarse al servidor VPN</p>
              </div>

              <div className="border-l-4 border-purple-500 bg-purple-50 p-4">
                <h4 className="font-semibold text-gray-900 mb-2">✓ Revisa el Balanceador de Carga</h4>
                <p className="text-sm text-gray-700">Si usas balanceo de carga, verifica que no esté causando problemas de conexión</p>
              </div>

              <div className="border-l-4 border-red-500 bg-red-50 p-4">
                <h4 className="font-semibold text-gray-900 mb-2">✓ Elimina la interfaz VPN e intenta de nuevo</h4>
                <p className="text-sm text-gray-700 mb-2">En tu Mikrotik, ve a Interfaces → L2TP Client → VPN ETWEB y elimínala. Luego ejecuta el script de nuevo.</p>
                <code className="text-xs bg-white px-2 py-1 rounded border">/interface l2tp-client remove [find name="VPN ETWEB"]</code>
              </div>

              <div className="border-l-4 border-orange-500 bg-orange-50 p-4">
                <h4 className="font-semibold text-gray-900 mb-2">✓ Contacta a tu Técnico</h4>
                <p className="text-sm text-gray-700">Si después de revisar todo lo anterior el problema persiste, contacta a tu técnico de confianza</p>
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => setStep('copy-script')}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Volver al Script
              </button>
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continuar Más Tarde
              </button>
            </div>
          </div>
        )}

        {/* Connection Step */}
        {step === 'connect' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-600 p-3 rounded-lg">
                <Server className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Conectar Router al Sistema</h2>
                <p className="text-gray-600">Ingresa los datos de conexión del router</p>
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <Server className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Conexión Automática Configurada</h4>
                  <p className="text-sm text-gray-700 mb-2">
                    El sistema se conectará automáticamente a tu router Mikrotik utilizando la configuración VPN que acabas de aplicar.
                  </p>
                  <p className="text-sm text-gray-700">
                    Haz clic en <strong>"Realizar Conexión"</strong> para que el sistema verifique y complete la configuración de tu router.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleTestConnection(); }} className="space-y-6">
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setStep('copy-script')}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Atrás
                </button>
                <button
                  type="submit"
                  disabled={connecting}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
                >
                  {connecting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Conectando...
                    </>
                  ) : (
                    <>
                      <Server className="w-5 h-5" />
                      Realizar Conexión
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Confirm Router Info Step */}
        {step === 'confirm' && routerInfo && (
          <div className={`rounded-xl shadow-lg p-8 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-center mb-6">
              <div className="inline-flex p-4 bg-blue-100 rounded-full mb-4">
                <Server className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ¡Conexión Establecida!
              </h2>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Revisa la información del router
              </p>
            </div>

            <div className={`rounded-lg p-6 mb-6 space-y-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nombre del Router (Identity) <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={editableRouterName}
                  onChange={(e) => setEditableRouterName(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold ${
                    isDark 
                      ? 'bg-gray-600 border-gray-500 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Ej: Router Principal, Hotspot Centro, etc."
                  required
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="text-red-600 font-semibold">* Requerido:</span> Asigna un nombre único para identificar este router en el sistema
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Modelo
                  </label>
                  <p className={`font-mono ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {routerInfo.platform}
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Versión
                  </label>
                  <p className={`font-mono ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {routerInfo.version}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Tiempo Activo
                  </label>
                  <p className={`font-mono ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {routerInfo.uptime}
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Carga CPU
                  </label>
                  <p className={`font-mono ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {routerInfo.cpu_load}%
                  </p>
                </div>
              </div>
            </div>

            {/* Validación de Configuración Hotspot */}
            {hotspotValidation && (
              <div className={`rounded-lg p-6 mb-6 border-2 ${
                hotspotValidation.all_ok 
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-red-50 border-red-300'
              }`}>
                <div className="flex items-start gap-3 mb-4">
                  {hotspotValidation.all_ok ? (
                    <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className={`text-lg font-bold mb-1 ${
                      hotspotValidation.all_ok ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {hotspotValidation.all_ok 
                        ? '✅ Configuración Hotspot Correcta' 
                        : '⚠️ Configuración Hotspot Requiere Ajustes'}
                    </h3>
                    <p className={`text-sm ${
                      hotspotValidation.all_ok ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {hotspotValidation.all_ok 
                        ? 'Tu Mikrotik está configurado correctamente para funcionar con EasyTickets' 
                        : 'Ajusta las siguientes configuraciones para que la plataforma funcione correctamente'}
                    </p>
                  </div>
                </div>

                {/* Detalles de validación */}
                {!hotspotValidation.all_ok && hotspotValidation.recommendations && (
                  <div className="mt-4 space-y-3">
                    <h4 className="font-semibold text-red-800 text-sm mb-2">Configuraciones a ajustar:</h4>
                    <div className="bg-white rounded-lg p-4 space-y-2">
                      {hotspotValidation.recommendations.map((recommendation, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="text-red-600 font-bold mt-0.5">•</span>
                          <span className="text-sm text-gray-800">{recommendation}</span>
                        </div>
                      ))}
                    </div>

                    {/* Instrucciones detalladas */}
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mt-4">
                      <p className="text-sm font-semibold text-yellow-800 mb-2">📝 Cómo corregir:</p>
                      <ol className="text-sm text-yellow-800 space-y-1 ml-4">
                        <li><strong>Hotspot Server:</strong> IP → Hotspot → Servers → Doble click en servidor → Cambiar "Addresses Per MAC" a <strong>1</strong></li>
                        <li><strong>Hotspot Profile:</strong> IP → Hotspot → Server Profiles → Doble click en perfil → Pestaña "Login"</li>
                        <li>✅ <strong>Habilitar:</strong> HTTP CHAP, HTTP PAP, MAC Cookie</li>
                        <li>❌ <strong>Deshabilitar:</strong> MAC, Cookie, HTTPS, Trial</li>
                      </ol>
                    </div>

                    <p className="text-xs text-gray-600 mt-3">
                      💡 <strong>Nota:</strong> Puedes continuar de todos modos, pero estas configuraciones son recomendadas para el correcto funcionamiento de EasyTickets.
                    </p>
                  </div>
                )}

                {/* Mostrar valores actuales */}
                {hotspotValidation.hotspot_server && (
                  <div className="mt-4">
                    <details className="text-sm">
                      <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">
                        Ver configuración actual del Mikrotik
                      </summary>
                      <div className="mt-2 bg-white rounded p-3 space-y-2 text-xs font-mono">
                        <p><strong>Hotspot Server:</strong> {hotspotValidation.hotspot_server.name || 'N/A'}</p>
                        <p><strong>Addresses Per MAC:</strong> {hotspotValidation.hotspot_server.addresses_per_mac || 'N/A'}</p>
                        {hotspotValidation.hotspot_profile && (
                          <>
                            <p className="mt-2"><strong>Hotspot Profile:</strong> {hotspotValidation.hotspot_profile.name || 'N/A'}</p>
                            <p><strong>Login By:</strong> {hotspotValidation.hotspot_profile.login_by || 'N/A'}</p>
                          </>
                        )}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setStep('connect')}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Atrás
              </button>
              <button
                type="button"
                onClick={handleConfirmRouter}
                disabled={connecting || !editableRouterName.trim()}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
              >
                {connecting ? 'Guardando...' : 'Aceptar y Continuar'}
              </button>
            </div>
          </div>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="inline-flex p-4 bg-green-100 rounded-full mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {reconnectMode ? '¡Reconexión Exitosa!' : '¡Router Configurado Exitosamente!'}
            </h2>
            <p className="text-gray-600 mb-8">
              {reconnectMode 
                ? 'El router ha sido reconectado y los planes sincronizados correctamente'
                : `Router ${currentRouter.router_index} está conectado y listo para usar`
              }
            </p>

            {reconnectMode ? (
              <button
                onClick={() => navigate('/routers')}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Volver a Routers
              </button>
            ) : currentRouterIndex < routers.length - 1 ? (
              <div>
                <p className="text-gray-700 mb-4">
                  Tienes {routers.length - currentRouterIndex - 1} router(es) más por configurar
                </p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={handleContinue}
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  >
                    Configurar Siguiente Router
                  </button>
                  <button
                    onClick={() => window.location.href = '/dashboard'}
                    className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Continuar Más Tarde
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Ir al Dashboard
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Modal de Reintentar (solo en modo reconexión) */}
      {showRetryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Conexión Fallida</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              No se pudo conectar al router. Verifica que el script VPN se haya ejecutado correctamente en tu Mikrotik.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRetryModal(false);
                  setStep('copy-script');
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Volver a Intentar
              </button>
              <button
                onClick={() => {
                  setShowRetryModal(false);
                  navigate('/routers');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouterSetupWizard;
